import React, { useState, useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import "react-datepicker/dist/react-datepicker.css";
import { Col, Row, FormGroup, FormControl } from "react-bootstrap";
import { FormControlLabel, Radio, RadioGroup, TextField } from "@mui/material";
import { MdOutlineModeEditOutline, MdSupervisedUserCircle, MdAccountCircle } from "react-icons/md";
import Alert from '@mui/material/Alert';
import SpecialistMapMarker from '../task/SpecialistMapMarker';
import SpecialistSearchFormFields from "./SpecialistSearchFormFields";
import SelectDate from "../forms/SelectDate";
import Lead from "../../shared/Lead";
import * as API from "../../api";
import Loading from "../../shared/Loading";
import Select from "../../shared/Select";
import TextArea from "../../shared/TextArea";
import Checkbox from "../../shared/Checkbox";
import SelectDuration from "./SelectDuration";
import SelectReferralInitiatedBy from "./SelectReferralInitiatedBy";
import PriorityBadge from "../../shared/PriorityBadge";
import SelectAssignee from "./SelectAssignee";
import SelectPcp from "../forms/SelectPcp";
import { Task, Settings } from "../../helpers/Contexts";
import RoomIcon from '@mui/icons-material/Room';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import GoogleMapReact from 'google-map-react';
import Patient from "../../shared/Patient";
import FieldError from "../../shared/FieldError";
import autocomplete from "autocompleter";

const SpecialistReferralForm = ({ state, task, dispatch, isCall, formHandler }) => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [specialists, setSpecialists] = useState([]);
  const [showInfo, setShowInfo] = useState(false);
  const [icd10Data, setICD10Data] = useState(null);
  const [selectedSpecialist, setSelectedSpecialist] = useState(null);
  const [selectedSpecialistGroup, setSelectedSpecialistGroup] = useState(null);
  const [selectedSpecialistPractice, setSelectedSpecialistPractice] = useState(null);
  const [searchedSpecialistPractices, setSearchedSpecialistPractices] = useState(null);
  const settings = useContext(Settings);
  const history = useHistory();
  const defaultProps = {
    center: {
      lat: task.patient.latitude ? parseFloat(task.patient.latitude) : 36.1716,
      lng: task.patient.longitude ? parseFloat(task.patient.longitude) : -115.1391
    },
    zoom: 11
  };
  const TaskSchema = Yup.object().shape({
    priority: Yup.string().oneOf(["low", "medium", "high"]).nullable(),
    type: Yup.string().required("Request type is required."),
    duration: Yup.string().required("Duration is required."),
    notes: Yup.string().required("notes is required"),
    order_referral_date: Yup.string().required("Referral Date is required"),
    specialist_type: Yup.string().required("Specialist Type is required."),
    specialist_practice_id: Yup.string().required("Specialist Practice is required."),
    specialist_id: Yup.string().required("Specialist is required."),
    referral_status: Yup.string().required("Referral Status is required."),
    ordering_provider_id: Yup.string().required("Ordering Provider is required."),
    referral_initiated_by: Yup.string(),
    retroactive: Yup.boolean().required(),
    assignee: Yup.string().required(
      "Assignee is required. Select a support group or agent to be assigned to this task."
    ),
  });

  useEffect(() => {
    try {
      var input = document.getElementById("icd10_code");
      autocomplete({
        input: input,
        debounceWaitMs: 1000,
        minLength: 3,
        fetch: async (text, update) => {
          let response = await API.searchICD10Api(text);
          if(response && response['data'] && response['data'][3]) {
            var suggestions = response['data'][3].map((n) => {
              return [n[0], n[1]];
            });
            setICD10Data(suggestions);
            update(suggestions);
          }
        },
        onSelect: function (item) {
          input.value = item.label;
        },
      });

    } catch(error) {
      console.log(error);
    }
  });

  return (
    <>
      <Formik
        initialValues={{ referral_status: "Routine",
          specialist_type: "",
          priority: "",
          type: "",
          assignee: "",
          notes: "",
          order_referral_date: "",
          referral_initiated_by: "",
          specialist_group_id: "",
          specialist_practice_id: "",
          specialist_id: "",
          ordering_provider_id: task.patient.pcp_id || "",
          duration: "",
          icd10_code: "",
          supporting_document: "",
          supporting_document_2: "",
          supporting_document_3: "",
          retroactive: false,
        }}
        onSubmit={(values) => {
          let request = {
            task_type: "specialist_referral",
            status: "open",
          };
          task.patient
            ? (request = {
                ...request,
                patient_id: task.patient.id,
              })
            : null;
          setLoading(true);
          API.postFormDataTask({
            task: {
              ...request,
              priority: values.priority,
              order_referral_date: values.order_referral_date,
              notes: values.notes,
              reason: values.reason,
              duration: values.duration,
              specialist_type: values.specialist_type,
              assigner_id: settings.me.id,
              ordering_provider_id: values.ordering_provider_id,
              assigner_support_group_id: settings.me.support_group_id,
              referral_status: values.referral_status,
              referral_initiated_by: values.referral_initiated_by,
              specialist_practice_id: values.specialist_practice_id,
              specialist_id: values.specialist_id,
              specialist_group_id: values.specialist_group_id,
              icd10_code: values.icd10_code,
              supporting_document: values.supporting_document,
              supporting_document_2: values.supporting_document_2,
              supporting_document_3: values.supporting_document_3,
              retroactive: !!values.retroactive,
            },
          }).then((res) => {
            setLoading(false);
            dispatch({ type: "set_loading", payload: true });
            history.push(`/physicians/tasks/item/${res.task.id}`);
          })
          .catch((err) => {
            setLoading(false);
            document.body.scrollTop = document.documentElement.scrollTop = 0;
            setError(err.error);
          });
        }}
      >
        {({ values, setFieldValue, isSubmitting, errors }) => {
          return (<>
            <Form>
              {
                error && (<>
                  <br />
                  <Alert severity="error">{ error }</Alert>
                </>)
              }
              <Row className=" flex pad20">
                { (!loading && (<Col md={7} className="form">
                    <Lead message={"New Specialist Referral"} onClick={(el) => setShowInfo(!showInfo) } />
                    { showInfo && (
                      <div className={'m-t-md specialist-referral-info m-t-lg'}>
                       <small>We can refer patients to specialists from a <b>specialist group </b> or an individual <b>specialist</b>.
                        <br />
                        The specialist referrals are only <b>valid for a certain duration.</b> Once this task is submitted,
                        wellPORTAL will generate a PDF copy of the referral which will be sent to the specialist or specialist group requested.
                        <br />
                        wellPORTAL will also generate a <b>Care Plan Requirement (CPR)</b> for the patient to complete with the specialist/specialist group.</small>
                      </div>)
                    }
                    <br />
                    <label>{"Ordering Provider"}</label>
                    <SelectPcp
                      valueLabel={ task.patient.pcp }
                      value={ task.patient.pcp_id }
                      name="ordering_provider_id"
                      pcpRequired={true}
                      active={false}
                      required={true}
                      setFieldValue={setFieldValue}
                    />
                    <SelectDate
                      fieldDescription="Date this referral was ordered"
                      fieldLabel="Referral Date"
                      fieldValueName="order_referral_date"
                      name="order_referral_date"
                      setFieldValue={setFieldValue}
                      values={values}
                      isNew={true}
                      dateType="specific"
                      required={true}
                    />
                    <Checkbox
                      name="retroactive"
                      onChange={(el) => {
                        setFieldValue('retroactive', el.target.checked);
                      }}
                      children={
                        <>
                          Retroactive referral?
                          <br />
                          <small>check this box if the referral start date is more than 2 weeks before the date you submitted this referral.</small>
                        </>
                      }
                    />
                    <br />
                    <SpecialistSearchFormFields
                      state={state}
                      task={task}
                      values={values}
                      setFieldValue={setFieldValue}
                     />

                    <SelectDuration required={true} value={values.duration} />
                    <SelectReferralInitiatedBy value={values.referral_initiated_by} />
                    <Select label="Referral Status" name="referral_status" value={values.referral_status} required={true}>
                      <option value={""}> {"Choose a Referral Status"}</option>
                      <option value={"Routine"}>Routine</option>
                      <option value={"Urgent"}>Urgent</option>
                    </Select>
                    <TextArea
                      label="Reason"
                      value={values.reason}
                      name="reason"
                      placeholder="Please write a reason"
                      required={true}
                    />
                    <label>Search for an ICD10</label>
                    <input
                      id={'icd10_code'}
                      type='text'
                      placeholder='Search for ICD10 Codes'
                      value={values.icd10_code}
                      onChange={(el) => {
                        setFieldValue('icd10_code', el.target.value);
                      }}
                    />
                    { icd10Data && (<>
                        { icd10Data.map((el, i) => {
                            return (
                              <div
                                key={i}
                                onClick={ (event) => {
                                  setFieldValue('icd10_code', el[0]);
                                  setICD10Data(null);
                                }}
                                className={'icd10-code-container'}
                                style={{ 'width': '100%', 'border': '1px solid #aaa', 'padding': '3px' }}>
                                { el[0] } - <b>{el[1]}</b>
                              </div>
                            );
                          })
                        }
                      </>)}
                    <br />
                    <label>Support Documents</label>
                    <span id="from">Attach supporting documents, if available.</span>
                    <FormGroup controlId="formFile" className="mb-3">
                      <FormControl
                        type="file"
                        onChange={(e) => {
                          setFieldValue("supporting_document", e.target.files[0])
                        }}
                      />
                    </FormGroup>
                    <FormGroup controlId="formFile" className="mb-3">
                      <FormControl
                        type="file"
                        onChange={(e) => {
                          setFieldValue("supporting_document_2", e.target.files[0])
                        }}
                      />
                    </FormGroup>
                    <FormGroup controlId="formFile" className="mb-3">
                      <FormControl
                        type="file"
                        onChange={(e) => {
                          setFieldValue("supporting_document_3", e.target.files[0])
                        }}
                      />
                    </FormGroup>
                    <button id="btn outline" type="submit">
                      Submit Specialist Referral Task
                    </button>
                  </Col>)
                )}
                <Col md={5}>
                  {!loading && task.patient && task.patient.id && (
                    <div className={'panel'}>
                      <div className='panel-body'>
                        <Patient patient={{ ...task.patient }} />
                      </div>
                    </div>
                  )}
                  {
                    !loading && selectedSpecialist && (
                      <div className="selected-specialist-container">
                        <label>Specialist: </label>
                        <b><PersonIcon />{selectedSpecialist.name}</b>
                        <br />
                        <i><LocalHospitalIcon />{selectedSpecialist.address}</i>
                        <br />
                        <i><PhoneIcon /> { selectedSpecialist.phone_number } </i>
                      </div>
                    )
                  }
                  {
                    !loading && selectedSpecialistGroup && selectedSpecialistGroup.name && selectedSpecialistGroup.name.length > 0 && (
                      <div className="selected-specialist-container">
                        <label>Specialist Group: </label>
                        <b><PersonIcon className='m-r-lg' /> {selectedSpecialistGroup.name}</b>
                        <br />
                        <i><LocalHospitalIcon className='m-r-lg' /> {selectedSpecialistGroup.address}</i>
                        <br />
                        <i><PhoneIcon className='m-r-lg' /> { selectedSpecialistGroup.phone_number} </i>
                      </div>
                    )
                  }
                  {
                    !loading && selectedSpecialistPractice && (
                      <div className="selected-specialist-container">
                        <label>Specialist Practice: </label>
                        <b><PersonIcon className='m-r-lg' />{selectedSpecialistPractice.name}</b>
                        <br />
                        <i><RoomIcon className='m-r-lg' />{selectedSpecialistPractice.address}</i>
                        <br />
                        <i><PhoneIcon className='m-r-lg' /> { selectedSpecialistPractice.phone_number} </i>
                      </div>
                    )
                  }
                  {!loading && searchedSpecialistPractices && (
                    <div style={{ height: '100vh', width: '100%' }}>
                      <GoogleMapReact
                        bootstrapURLKeys={{ key: `${settings.me.google_maps_api_key}` }}
                        defaultCenter={defaultProps.center}
                        defaultZoom={defaultProps.zoom}
                        yesIWantToUseGoogleMapApiInternals
                      >
                        {
                          searchedSpecialistPractices.map((practice, idx) => {
                            if (practice.latitude && practice.longitude) {
                              return <SpecialistMapMarker
                                key={idx}
                                selectable={false}
                                lat={parseFloat(practice.latitude)}
                                lng={parseFloat(practice.longitude)}
                                practice={practice}
                              />;
                            }
                          })
                        }
                      </GoogleMapReact>
                    </div>
                    )
                  }
                </Col>
              </Row>
              {loading && <Loading message={"Creating Task "} />}
            </Form>
          </>);
          }}
      </Formik>
    </>
  );
};

export default SpecialistReferralForm;
